import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, SaveConfigurationSourceDataMap } from "@/api/dtos";
import Form from "@/components/forms/Form";
import ConfigurationSourceDataFormBanner from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataFormBanner";
import ConfigurationSourceDataFormDetails from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataFormDetails";
import ConfigurationSourceDataFormNavigation from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataFormNavigation";
import IsLoading from "@/components/layouts/IsLoading";
import SetLinks from "@/components/layouts/SetLinks";
import Show from "@/components/layouts/Show";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationSourceDatasTrialCreateLink } from "@/contexts/layouts/LinkContext";
import { FormError } from "@/errors";
import useConfigurationSourceDataMap from "@/hooks/data/useConfigurationSourceDataMap";
import useNewConfigurationSourceData from "@/hooks/data/useNewConfigurationSourceData";
import FormErrorType from "@/types/form/FormErrorType";
import asyncDebounce from "@/utilities/asyncDebounce";
import { Box, Typography } from "@mui/material";
import { IFormSubmit, IFormSubmitFailed, IFormSubmitValidationFailed, IFormValidate, ValidateOn } from "@ngt/forms-core";
import { useSnackbar } from "notistack";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

export interface IConfigurationSourceDataFormState {
    configuration?: ConfigurationSourceDataMap;
    trialConfiguration?: ConfigurationSourceDataMap;
}

const ConfigurationSourceData: FC<{ global?: boolean; insert?: boolean; }> = ({ global, insert }) => {
    const { enqueueSnackbar } = useSnackbar();
    const client = useContext(ServiceStackClientContext);
    const navigate = useNavigate();
    
    const { data: trial } = useContext(TrialContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: metadata } = useContext(MetadataContext);
    const configurationSourceDataId = useParams().configurationSourceDataId;

    const { data: newConfiguration, loading: newConfigurationLoading, validating: newConfigurationValidating } = useNewConfigurationSourceData(!global && insert && trial?.id ? trial.id : undefined);
    const { data: sourceData, loading: sourceDataLoading, validating: sourceDataValidating, mutate } = useConfigurationSourceDataMap(trial?.id, !global && !insert && configurationSourceDataId ? +configurationSourceDataId : undefined);

    const loading = !global ? 
            !insert ?
                configurationSourceDataId ? 
                    sourceDataLoading : 
                    false :
                trial?.id ?
                    newConfigurationLoading :
                    false :
            false;
    const validating = !global ? 
        !insert ?
            configurationSourceDataId ? 
                sourceDataValidating : 
                false :
            trial?.id ?
                newConfigurationValidating :
                false :
        false;

    const initialValue = useMemo(() => {
        return {
            configuration: insert ?
                newConfiguration :
                global ? 
                    trialConfiguration : 
                    sourceData,
            trialConfiguration: trialConfiguration
        } as IConfigurationSourceDataFormState
    }, [sourceData, trialConfiguration, configurationSourceDataId, global, insert, newConfiguration])

    const submit = useCallback<IFormSubmit<IConfigurationSourceDataFormState, FormError>>(async (formState, formActions) => {
        const response = await client.post(new SaveConfigurationSourceDataMap({ data: formState.value?.configuration }));

        enqueueSnackbar(`Source Data Configuration successfully saved!`, { variant: "success" });

        if (insert) {
            navigate(`/${trial?.oid}/configuration/source-data/${response.data?.configuration?.id}`)
        }
        else {
            await mutate(response);
        }

        return {};
    }, [client, enqueueSnackbar, mutate, insert, navigate, trial]);

    const submitFailed = useCallback<IFormSubmitFailed<IConfigurationSourceDataFormState, FormError>>(async (formState, formActions, submitException) => {
        
        enqueueSnackbar(`An error occurred while attempting to save the Source Data Configuration!`, { variant: "error" });

        return {};
    }, [enqueueSnackbar]);

    const submitValidationFailed = useCallback<IFormSubmitValidationFailed<IConfigurationSourceDataFormState, FormError>>(async (formState, formActions, validationException) => {
        enqueueSnackbar("The form contains invalid data, please correct the marked fields and try again!", { variant: "warning" });

        return formState.errors;
    }, [enqueueSnackbar]);

    
    const validate = useMemo<IFormValidate<IConfigurationSourceDataFormState, FormError>>(() => {
        return asyncDebounce(async (formState, formActions) => {
            let errors: Record<string, FormError[]> = {};

            if (!formState?.value?.configuration?.configuration?.name) {
                errors['configuration.configuraiton.name'] = [
                    {
                        property: 'configuration.configuraiton.name',
                        message: 'Name must be provided!',
                        type: FormErrorType.Normal
                    }
                ];
            }

            return errors;
        }, 500)
    }, []);

    return (
        <Form
            initialValue={initialValue}
            checkForClasses={false}
            debug={AppSettings.debug}
            onSubmit={submit}
            onSubmitFailed={submitFailed}
            onSubmitValidationFailed={submitValidationFailed}
            onValidate={validate}
            validateOn={ValidateOn.onChange}
        >
            <ConfigurationSourceDataFormBanner />
            <SetLinks 
                createTrialLink={ConfigurationSourceDatasTrialCreateLink} 
                institutionLinkEnabled={false} 
                participantLinkEnabled={false} 
            />
            <IsLoading name="configuration-source-data-page" loading={loading} validating={validating} />
            <Show show={!loading}>
                    <Typography variant="h1" gutterBottom>
                        Configuration &mdash; Source Data
                        <Box
                            sx={{
                                fontSize: '2rem'
                            }}
                        >
                            {sourceData?.configuration?.name}
                        </Box>
                    </Typography>
                    <ConfigurationSourceDataFormDetails />
                    <Outlet />
            </Show>
        </Form>
    )
};

export default ConfigurationSourceData;